import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Settings2, Package2 } from 'lucide-react';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

export default function BulkSampleInvoiceForm({ products, fullsetList, bahanList, bibitSampleList, onChange }) {
  // Global defaults
  const [defaultUkuran, setDefaultUkuran] = useState(10);
  const [defaultBotolId, setDefaultBotolId] = useState('');
  const [defaultPelarutId, setDefaultPelarutId] = useState('');
  const [defaultPersenBibit, setDefaultPersenBibit] = useState(25);

  const pelarutOptions = bahanList.filter(b => (b.kategori || '').toLowerCase() === 'alkohol' || (b.nama || '').toLowerCase().includes('alkohol'));
  const bibitOptions = bibitSampleList || bahanList.filter(b => (b.kategori || '').toLowerCase() === 'bibit');

  const vendorOptions = [...new Set(bibitOptions.map(b => b.vendor).filter(Boolean))].sort();

  const handleBotolChange = (val, isDefault = false, idx = -1) => {
    const fullset = fullsetList?.find(f => f.id === val);
    const size = fullset ? (fullset.ukuran_botol_ml || fullset.ukuran_ml || fullset.volume_ml || fullset.ukuran || 0) : '';

    if (isDefault) {
      setDefaultBotolId(val);
      if (size) setDefaultUkuran(Number(size));
    } else {
      const updates = { botol_id: val, botol_dari_client: false };
      if (size) {
        updates.botol_ukuran_client = Number(size);
        updates.botol_ukuran_label_ml = Number(size);
      }
      updateRow(idx, updates);
    }
  };

  // Helper to calculate HPP breakdown
  const calcHpp = (ukuran, botolId, pelarutId, persenBibit) => {
    let botolPrice = 0;
    if (botolId) {
      const botol = fullsetList?.find(b => b.id === botolId);
      botolPrice = botol ? (botol.harga_jual || (botol.harga_rupiah ? botol.harga_rupiah * 1.3 : 0)) : 0;
    }

    let pelarutPrice = 0;
    if (pelarutId) {
      const pelarut = pelarutOptions.find(b => b.id === pelarutId);
      if (pelarut) {
        const hrgKg = pelarut.harga_jual || (pelarut.harga_rupiah ? pelarut.harga_rupiah * 1.3 : 0);
        const hrgMl = hrgKg / 1000;
        const volPelarut = (ukuran * (100 - persenBibit)) / 100;
        pelarutPrice = volPelarut * hrgMl;
      }
    }
    
    return {
      kemasan: Math.round(botolPrice),
      cairan: Math.round(pelarutPrice),
      total: Math.round(botolPrice + pelarutPrice)
    };
  };

  // When adding a new row, use defaults
  const addRow = () => {
    const hpp = calcHpp(defaultUkuran, defaultBotolId, defaultPelarutId, defaultPersenBibit);
    const initialJual = hpp.total + 3000;
    
    const botol = fullsetList?.find(b => b.id === defaultBotolId);
    const pelarut = pelarutOptions?.find(b => b.id === defaultPelarutId);

    const botolPrice = botol ? (botol.harga_jual || (botol.harga_rupiah ? botol.harga_rupiah * 1.3 : 0)) : 0;
    const pelarutHrgKg = pelarut ? (pelarut.harga_jual || (pelarut.harga_rupiah ? pelarut.harga_rupiah * 1.3 : 0)) : 0;
    
    const newProduct = {
      nama_product: '',
      qty: 1,
      botol_ukuran_label_ml: defaultUkuran,
      botol_ukuran_client: defaultUkuran, 
      botol_dari_client: !botol, 
      botol_id: defaultBotolId,
      botol_nama: botol ? botol.nama : '',
      is_fullset: !!botol,
      fullset_nama: botol ? botol.nama : '',
      fullset_harga_beli: Math.round(botolPrice),
      pelarut_id: defaultPelarutId,
      persen_bibit: defaultPersenBibit,
      hpp_kemasan: hpp.kemasan,
      hpp_cairan: hpp.cairan,
      hpp_satuan: hpp.total,
      harga_jual_per_pcs: initialJual,
      final_harga_jual_per_pcs: initialJual,
      total_harga_jual: initialJual,
      racikan: [
        { bahan_id: defaultPelarutId, bahan_nama: pelarut ? pelarut.nama : 'Pelarut Belum Dipilih', vendor: pelarut ? pelarut.vendor : '', kategori: 'Pelarut', persentase: 100 - defaultPersenBibit, hpp_bahan: pelarutHrgKg },
        { bahan_nama: 'Bibit Belum Dipilih', vendor: '', kategori: 'Bibit', persentase: defaultPersenBibit, is_sample: true, hpp_bahan: 0 }
      ]
    };
    onChange([...products, newProduct]);
  };

  const updateRow = (idx, updates) => {
    const updated = [...products];
    const item = { ...updated[idx], ...updates };
    
    // Recalculate price if relevant fields changed
    if ('botol_ukuran_client' in updates || 'botol_id' in updates || 'pelarut_id' in updates || 'persen_bibit' in updates || 'nama_product' in updates) {
       const hpp = calcHpp(
         item.botol_ukuran_client || 0,
         item.botol_id,
         item.pelarut_id,
         item.persen_bibit || 0
       );
       item.hpp_kemasan = hpp.kemasan;
       item.hpp_cairan = hpp.cairan;
       item.hpp_satuan = hpp.total;
       
       if (!item.harga_jual_per_pcs || item.harga_jual_per_pcs < hpp.total) {
         item.harga_jual_per_pcs = hpp.total + 3000;
       }
       
       const botol = fullsetList?.find(b => b.id === item.botol_id);
       const pelarut = pelarutOptions?.find(b => b.id === item.pelarut_id);
       const bibitObj = bibitOptions?.find(b => (b.nama || '').trim() === (item.nama_product || '').trim());
       
       const botolPrice = botol ? (botol.harga_jual || (botol.harga_rupiah ? botol.harga_rupiah * 1.3 : 0)) : 0;
       const pelarutHrgKg = pelarut ? (pelarut.harga_jual || (pelarut.harga_rupiah ? pelarut.harga_rupiah * 1.3 : 0)) : 0;
       const bibitHrgKg = bibitObj ? (bibitObj.harga_jual || (bibitObj.harga_rupiah ? bibitObj.harga_rupiah * 1.3 : 0)) : 0;

       item.botol_nama = botol ? botol.nama : '';
       item.botol_dari_client = !botol;
       item.is_fullset = !!botol;
       item.fullset_nama = botol ? botol.nama : '';
       item.fullset_harga_beli = Math.round(botolPrice);

       item.racikan = [
         { bahan_id: item.pelarut_id, bahan_nama: pelarut ? pelarut.nama : 'Pelarut Belum Dipilih', vendor: pelarut ? pelarut.vendor : '', kategori: 'Pelarut', persentase: 100 - (item.persen_bibit || 0), hpp_bahan: pelarutHrgKg },
         { bahan_nama: item.nama_product || 'Bibit Belum Dipilih', vendor: bibitObj ? bibitObj.vendor : '', kategori: 'Bibit', persentase: item.persen_bibit || 0, is_sample: true, hpp_bahan: 0 }
       ];
    }
    
    if ('harga_jual_per_pcs' in updates || 'qty' in updates || 'botol_ukuran_client' in updates || 'botol_id' in updates || 'pelarut_id' in updates || 'persen_bibit' in updates) {
       item.final_harga_jual_per_pcs = item.harga_jual_per_pcs || 0;
       item.total_harga_jual = (item.final_harga_jual_per_pcs || 0) * (item.qty || 1);
    }

    updated[idx] = item;
    onChange(updated);
  };

  const removeRow = (idx) => {
    const updated = products.filter((_, i) => i !== idx);
    onChange(updated);
  };

  return (
    <div className="bg-white border border-[#E6D5B8] rounded-2xl p-5 shadow-sm space-y-6">
      
      {/* Defaults Section */}
      <div className="bg-[#F9F5EC] p-4 rounded-xl border border-[#E6D5B8]/50">
        <h3 className="text-xs font-black text-[#8C6208] flex items-center gap-2 mb-3 uppercase tracking-wider">
          <Settings2 className="w-4 h-4" /> Pengaturan Default (Cepat)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-bold text-[#7A695B] block mb-1">Ukuran (ml)</label>
            <Input type="number" value={defaultUkuran} onChange={e => setDefaultUkuran(Number(e.target.value))} className="h-8 text-xs bg-white" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[#7A695B] block mb-1">Botol (Wadah)</label>
            <SearchableCombobox
              value={defaultBotolId}
              onValueChange={v => handleBotolChange(v, true)}
              options={(fullsetList || []).map(b => ({ value: b.id, label: b.nama }))}
              placeholder="Pilih Fullset"
              className="h-8 text-xs bg-white"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[#7A695B] block mb-1">Pelarut</label>
            <SearchableCombobox
              value={defaultPelarutId}
              onValueChange={setDefaultPelarutId}
              options={pelarutOptions.map(b => ({ value: b.id, label: b.nama }))}
              placeholder="Pilih Pelarut"
              className="h-8 text-xs bg-white"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[#7A695B] block mb-1">Persentase Bibit (%)</label>
            <Input type="number" value={defaultPersenBibit} onChange={e => setDefaultPersenBibit(Number(e.target.value))} className="h-8 text-xs bg-white" />
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mt-2">Setiap baris baru yang ditambahkan akan otomatis menggunakan pengaturan ini. Anda tetap bisa merubahnya per baris.</p>
      </div>

      {/* Table Section */}
      <div>
        <h3 className="text-sm font-semibold text-[#302621] flex items-center gap-2 mb-3">
          <Package2 className="w-4 h-4 text-[#8C6208]" /> Daftar Sample
        </h3>
        <div className="overflow-x-auto border border-[#E6D5B8]/40 rounded-xl">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#FFFDF9] border-b border-[#E6D5B8]/40">
              <tr>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider w-8">#</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider min-w-[180px]">Vendor & Nama Bibit</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider w-20">Qty</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider w-24">Ukuran (ml)</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider min-w-[120px]">Botol (Fullset)</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider min-w-[120px]">Pelarut</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider w-24">Bibit (%)</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider w-24 text-right">HPP Satuan</th>
                <th className="px-3 py-2 font-bold text-[#7A695B] uppercase tracking-wider w-32 text-right">Harga Jual / Pcs</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {products.map((p, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-center text-slate-400 font-bold">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <div className="space-y-1.5">
                      <SearchableCombobox
                        value={p.vendor_bibit || ''}
                        onValueChange={v => updateRow(idx, { vendor_bibit: v, nama_product: '' })}
                        options={vendorOptions.map(v => ({ value: v, label: v }))}
                        placeholder="Pilih Vendor..."
                        className="w-full h-7 text-[9px] bg-[#F9F5EC] text-[#8C6208] font-semibold"
                      />
                      <SearchableCombobox 
                        value={bibitOptions.find(b => (b.nama || '').trim() === (p.nama_product || '').trim())?.id || p.nama_product || ''} 
                        onValueChange={v => {
                          const bibit = bibitOptions.find(b => b.id === v);
                          const bibitName = bibit ? bibit.nama : v;
                          updateRow(idx, { nama_product: bibitName });
                        }}
                        options={bibitOptions.filter(b => b.vendor === p.vendor_bibit).map(b => ({ value: b.id, label: b.nama }))}
                        placeholder={p.vendor_bibit ? 'Pilih Bibit...' : 'Pilih Vendor Dulu'}
                        className={`w-full h-7 text-[10px] bg-white ${!p.vendor_bibit ? 'opacity-50 pointer-events-none' : ''}`}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Input 
                      type="number"
                      value={p.qty || 1} 
                      onChange={e => updateRow(idx, { qty: Number(e.target.value) })}
                      className="h-7 text-xs px-2 border-[#E6D5B8]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input 
                      type="number"
                      value={p.botol_ukuran_client || ''} 
                      onChange={e => updateRow(idx, { botol_ukuran_client: Number(e.target.value) })}
                      className="h-7 text-xs px-2 border-[#E6D5B8]"
                    />
                  </td>
                  <td className="px-3 py-2">
                      <SearchableCombobox 
                        value={p.botol_id || ''} 
                        onValueChange={v => handleBotolChange(v, false, idx)}
                        options={(fullsetList || []).map(b => ({ value: b.id, label: b.nama }))}
                        placeholder="Pilih Fullset..."
                        className="w-full h-7 text-[10px] bg-white"
                      />
                  </td>
                  <td className="px-3 py-2">
                      <SearchableCombobox 
                        value={p.pelarut_id || ''} 
                        onValueChange={v => updateRow(idx, { pelarut_id: v })}
                        options={pelarutOptions.map(b => ({ value: b.id, label: b.nama }))}
                        placeholder="Pilih Pelarut..."
                        className="w-full h-7 text-[10px] bg-white"
                      />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <Input 
                        type="number"
                        value={p.persen_bibit || ''} 
                        onChange={e => updateRow(idx, { persen_bibit: Number(e.target.value) })}
                        className="h-7 text-xs px-2 border-[#E6D5B8] w-14"
                      />
                      <span className="text-slate-400 font-bold">%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-right">
                      <p className="font-bold text-slate-700">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(p.hpp_satuan !== undefined ? p.hpp_satuan : calcHpp(p.botol_ukuran_client||0, p.botol_id, p.pelarut_id, p.persen_bibit||0).total)}</p>
                      <p className="text-[9px] text-slate-400 font-medium leading-tight mt-0.5">
                        Kms: Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(p.hpp_kemasan !== undefined ? p.hpp_kemasan : calcHpp(p.botol_ukuran_client||0, p.botol_id, p.pelarut_id, p.persen_bibit||0).kemasan)} <br/>
                        Crn: Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(p.hpp_cairan !== undefined ? p.hpp_cairan : calcHpp(p.botol_ukuran_client||0, p.botol_id, p.pelarut_id, p.persen_bibit||0).cairan)}
                      </p>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="space-y-1.5">
                      <Input 
                        type="number"
                        value={p.harga_jual_per_pcs || ''} 
                        onChange={e => updateRow(idx, { harga_jual_per_pcs: Number(e.target.value) })}
                        className="h-7 text-xs px-2 border-[#E6D5B8] text-right font-black text-emerald-700 bg-emerald-50/30 focus:bg-white"
                        placeholder="Harga Jual"
                      />
                      <div className="text-right">
                        <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-md border border-emerald-200 shadow-sm">
                          Mrg: Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((p.harga_jual_per_pcs || 0) - (p.hpp_satuan !== undefined ? p.hpp_satuan : calcHpp(p.botol_ukuran_client||0, p.botol_id, p.pelarut_id, p.persen_bibit||0).total))}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Button variant="ghost" size="icon" onClick={() => removeRow(idx)} disabled={products.length <= 1} className="w-6 h-6 text-red-400 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Button onClick={addRow} variant="outline" className="text-[#8C6208] border-dashed border-[#D4AF37] hover:bg-[#F9F5EC] gap-2 h-9 text-xs font-bold w-full">
            <Plus className="w-4 h-4" /> Tambah Baris Sample
          </Button>
        </div>
      </div>

    </div>
  );
}
